import { 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Typography,
  Divider,
  Chip,
  Box,
  CircularProgress
} from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useRecordContext, useGetOne, useDataProvider, useNotify, useRefresh } from 'react-admin';
import { Funeral } from '../types';
import moment from 'moment-timezone';

// SP3 documents are issued in Quebec; the "Date et heure du décès" field is
// recorded in local Eastern time. Treat the raw extracted string as
// America/Toronto and only convert to ISO UTC when persisting.
const FUNERAL_TZ = 'America/Toronto';
const DEATH_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const formatDeathTimeInTz = (value: Date | string | null | undefined): string => {
  if (!value) return '-';
  const m = moment(value);
  if (!m.isValid()) return '-';
  return m.tz(FUNERAL_TZ).format(DEATH_TIME_FORMAT);
};

const torontoLocalToIso = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const m = moment.tz(raw, [DEATH_TIME_FORMAT, moment.ISO_8601], FUNERAL_TZ);
  return m.isValid() ? m.toISOString() : null;
};

interface SP3DataViewerProps {
  sp3Data: Record<string, any> | null;
  autoOpen?: boolean;
  onClose?: () => void;
  hideButton?: boolean;
}

// Helper function to format address objects
const formatAddressObject = (addressObj: any): string => {
  if (!addressObj || typeof addressObj !== 'object') return '';

  const parts = [];
  if (addressObj.adresse) parts.push(addressObj.adresse);
  if (addressObj.municipalite) parts.push(addressObj.municipalite);
  if (addressObj.province) parts.push(addressObj.province);
  if (addressObj.codePostal) parts.push(addressObj.codePostal);
  if (addressObj.pays) parts.push(addressObj.pays);

  return parts.join(', ');
};

// The SP3/SIED given-name field carries "First Middle" as one string. Split it
// into our first_name (first token) + middle_name (the remainder). Hyphenated
// given names (e.g. "Jean-Baptiste") stay in the first token.
const splitGivenName = (
  given: string | null | undefined,
): { first_name: string; middle_name: string | null } => {
  const tokens = String(given ?? '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { first_name: '', middle_name: null };
  return {
    first_name: tokens[0],
    middle_name: tokens.slice(1).join(' ') || null,
  };
};

export const SP3DataViewer = ({ sp3Data, autoOpen = false, onClose, hideButton = false }: SP3DataViewerProps) => {
  const [open, setOpen] = useState(false);
  
  // Auto-open the modal when autoOpen prop is true
  useEffect(() => {
    if (autoOpen && sp3Data) {
      setOpen(true);
    }
  }, [autoOpen, sp3Data]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const record = useRecordContext<Funeral>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  
  const { data: deceasedClient } = useGetOne(
    'clients',
    { id: record?.deceased_client_id },
    { enabled: !!record?.deceased_client_id }
  );

  const autoAppliedRef = useRef(false);
  useEffect(() => {
    if (autoAppliedRef.current) return;
    if (!sp3Data || !record || !deceasedClient) return;

    let data: any = sp3Data;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch { return; }
    }
    if (data?.extractedData) data = data.extractedData;
    if (!data) return;

    const sp3Number = data.deceased_person?.sp3_number;
    const medicareNumber = data.deceased_person?.medicare_number;
    const doctorName = data.medical_certification?.doctor_name;

    const funeralUpdates: any = { id: record.id };
    if (!record.sp3_number && sp3Number) funeralUpdates.sp3_number = sp3Number;
    if (!record.doctor_name && doctorName) funeralUpdates.doctor_name = doctorName;

    const clientUpdates: any = { id: deceasedClient.id };
    if (!deceasedClient.medicare_number && medicareNumber) clientUpdates.medicare_number = medicareNumber;

    const funeralHasUpdates = Object.keys(funeralUpdates).length > 1;
    const clientHasUpdates = Object.keys(clientUpdates).length > 1;
    if (!funeralHasUpdates && !clientHasUpdates) return;

    autoAppliedRef.current = true;
    void (async () => {
      try {
        if (funeralHasUpdates) {
          await dataProvider.update('funerals', {
            id: record.id,
            data: funeralUpdates,
            previousData: record,
          });
        }
        if (clientHasUpdates) {
          await dataProvider.update('clients', {
            id: deceasedClient.id,
            data: clientUpdates,
            previousData: deceasedClient,
          });
        }
        const applied = [
          funeralUpdates.sp3_number && 'SP3 number',
          clientUpdates.medicare_number && 'medicare number',
          funeralUpdates.doctor_name && 'doctor name',
        ].filter(Boolean) as string[];
        if (applied.length > 0) {
          notify(`Auto-filled from SP3: ${applied.join(', ')}`, { type: 'info' });
          refresh();
        }
      } catch (error) {
        autoAppliedRef.current = false;
        console.error('Auto-apply SP3 fields failed:', error);
      }
    })();
  }, [sp3Data, record, deceasedClient, dataProvider, notify, refresh]);

  const handleOpen = () => {
    // console.log('Current Funeral Record:', record);
    // console.log('Deceased Client:', deceasedClient);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };
  const handleConfirmOpen = () => setConfirmOpen(true);
  const handleConfirmClose = () => setConfirmOpen(false);

  if (!sp3Data) {
    return null;
  }

  let parsedData = sp3Data;
  if (typeof sp3Data === 'string') {
    try {
      parsedData = JSON.parse(sp3Data);
    } catch (e) {
      console.error('Failed to parse SP3 data:', e);
      return null;
    }
  }

  // Handle new structure where data is nested under extractedData
  if (parsedData && parsedData.extractedData) {
    parsedData = parsedData.extractedData;
  }

  // Check if this is the old flat format (contains fields like dateNaissance, sexe, etc.)
  const isOldFormat = parsedData && (
    parsedData.dateNaissance ||
    parsedData.sexe ||
    parsedData.nomDefunt ||
    parsedData.dateHeureDeces
  );

  if (isOldFormat) {
    // Helper function to parse names in "Last name, First name" format
    const parseOldName = (fullName: string | null): { firstName: string | null; lastName: string | null } => {
      if (!fullName) return { firstName: null, lastName: null };
      
      const parts = fullName.split(',').map(p => p.trim());
      if (parts.length === 2) {
        return { lastName: parts[0], firstName: parts[1] };
      }
      
      const spaceParts = fullName.split(' ');
      return { firstName: spaceParts[0] || null, lastName: spaceParts.slice(1).join(' ') || null };
    };
    
    // Helper to clean form number
    const cleanOldFormNumber = (formNumber: string | null): string | null => {
      if (!formNumber) return null;
      return formNumber.replace(/^\d+\s*-\s*/, '').trim();
    };
    
    // Helper to combine place of birth
    const combinePlaceOfBirth = (province: string | null, country: string | null): string | null => {
      const parts = [];
      if (province) parts.push(province);
      if (country) parts.push(country);
      return parts.length > 0 ? parts.join(', ') : null;
    };
    
    const deceasedName = parseOldName(parsedData.nomDefunt);
    const fatherName = parseOldName(parsedData.pere);
    const motherName = parseOldName(parsedData.mere);
    
    // Convert old format to new organized format
    parsedData = {
      deceased_person: {
        first_name: deceasedName.firstName,
        last_name: deceasedName.lastName,
        date_of_birth: parsedData.dateNaissance || null,
        sex: parsedData.sexe || null,
        place_of_birth: combinePlaceOfBirth(parsedData.provinceNaissance, parsedData.paysNaissance),
        medicare_number: parsedData.numeroAssurance || null,
        sp3_number: cleanOldFormNumber(parsedData.numeroFormulaire)
      },
      parent_info: {
        father_first_name: fatherName.firstName,
        father_last_name: fatherName.lastName,
        mother_first_name: motherName.firstName,
        mother_last_name: motherName.lastName
      },
      place_of_death: {
        location: parsedData.endroitDeces || null,
        code: parsedData.codeLieu || null,
        address: parsedData.adresseDeces || null,
        date_time_of_death: parsedData.dateHeureDeces || null
      },
      medical_certification: {
        doctor_name: parsedData.auteur?.nom || null
      }
    };
  }

  // Debug logging
  console.log('SP3DataViewer - Raw sp3Data:', sp3Data);
  console.log('SP3DataViewer - Parsed data:', parsedData);
  console.log('SP3DataViewer - Is old format:', isOldFormat);
  console.log('SP3DataViewer - Available sections:', Object.keys(parsedData || {}));

  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return '-';
    return moment(date).format('YYYY-MM-DD');
  };

  const formatDateForComparison = (date: Date | string | null | undefined): string => {
    if (!date) return '-';
    // Format both database timestamps and simple date strings to YYYY-MM-DD for comparison
    return moment(date).format('YYYY-MM-DD');
  };

  const getCurrentValue = (section: string, field: string): string => {
    if (!record) return '-';


    switch (section) {
      case 'Deceased Person':
      case 'deceased_person':
        switch (field) {
          case 'first_name':
            // SP3 given name is "First Middle"; compare against our combined value.
            return (
              [deceasedClient?.first_name, deceasedClient?.middle_name]
                .filter(Boolean)
                .join(' ') || '-'
            );
          case 'last_name':
            return deceasedClient?.last_name || '-';
          case 'date_of_birth':
            return formatDate(deceasedClient?.birthdate);
          case 'sex':
            return deceasedClient?.sex || '-';
          case 'place_of_birth':
            return deceasedClient?.place_of_birth || '-';
          case 'medicare_number':
            return deceasedClient?.medicare_number || '-';
          case 'sp3_number':
            return record.sp3_number || '-';
          default:
            return '-';
        }
      case 'Place of Death':
      case 'place_of_death':
        switch (field) {
          case 'location':
            return record.place_of_death || '-';
          case 'code':
            return record.place_of_death_code || '-';
          case 'address':
            // Handle JSON object or string
            if (typeof record.place_of_death_address === 'object' && record.place_of_death_address !== null) {
              return formatAddressObject(record.place_of_death_address);
            }
            return record.place_of_death_address || '-';
          case 'date_time_of_death':
            return formatDeathTimeInTz(record.date_time_of_death);
          default:
            return '-';
        }
      case 'Medical Certification':
      case 'medical_certification':
        switch (field) {
          case 'doctor_name':
            return record.doctor_name || '-';
          default:
            return '-';
        }
      case 'Parent Information':
      case 'parent_info':
        switch (field) {
          case 'father_first_name':
            return deceasedClient?.parent1_first_name || '-';
          case 'father_last_name':
            return deceasedClient?.parent1_last_name || '-';
          case 'mother_first_name':
            return deceasedClient?.parent2_first_name || '-';
          case 'mother_last_name':
            return deceasedClient?.parent2_last_name || '-';
          default:
            return '-';
        }
      default:
        return '-';
    }
  };

  const applyValue = async (section: string, field: string, value: any) => {
    if (!record || !deceasedClient) return;
    
    setApplying(true);
    try {
      const sectionKey = section.toLowerCase().replace(/ /g, '_');
      
      // Determine which resource to update (funeral or client)
      if (sectionKey === 'deceased_person' || sectionKey === 'parent_info') {
        // Update client record
        const updateData: any = { id: deceasedClient.id };
        
        switch (field) {
          case 'first_name': {
            // SP3 given name may be "First Middle" — split across both columns.
            const parts = splitGivenName(value);
            updateData.first_name = parts.first_name;
            updateData.middle_name = parts.middle_name;
            break;
          }
          case 'last_name':
            updateData.last_name = value;
            break;
          case 'date_of_birth':
            // Ensure date is in YYYY-MM-DD format
            updateData.birthdate = moment(value).format('YYYY-MM-DD');
            break;
          case 'sex':
            updateData.sex = value;
            break;
          case 'place_of_birth':
            updateData.place_of_birth = value;
            break;
          case 'medicare_number':
            updateData.medicare_number = value;
            break;
          case 'father_first_name':
            updateData.parent1_first_name = value;
            break;
          case 'father_last_name':
            updateData.parent1_last_name = value;
            break;
          case 'mother_first_name':
            updateData.parent2_first_name = value;
            break;
          case 'mother_last_name':
            updateData.parent2_last_name = value;
            break;
          case 'sp3_number':
            // SP3 number goes to funeral record, not client
            await dataProvider.update('funerals', {
              id: record.id,
              data: { id: record.id, sp3_number: value },
              previousData: record
            });
            notify(`Updated ${field.replace(/_/g, ' ')}`, { type: 'success' });
            refresh();
            setApplying(false);
            return;
        }
        
        await dataProvider.update('clients', {
          id: deceasedClient.id,
          data: updateData,
          previousData: deceasedClient
        });
        
        notify(`Updated ${field.replace(/_/g, ' ')}`, { type: 'success' });
      } else {
        // Update funeral record
        const updateData: any = { id: record.id };
        
        switch (field) {
          case 'location':
            updateData.place_of_death = value;
            break;
          case 'code':
            updateData.place_of_death_code = value;
            break;
          case 'address':
            // Store as JSON object
            updateData.place_of_death_address = value;
            break;
          case 'doctor_name':
            updateData.doctor_name = value;
            break;
          case 'date_time_of_death': {
            const iso = torontoLocalToIso(value);
            if (iso) updateData.date_time_of_death = iso;
            break;
          }
        }

        await dataProvider.update('funerals', {
          id: record.id,
          data: updateData,
          previousData: record
        });
        
        notify(`Updated ${field.replace(/_/g, ' ')}`, { type: 'success' });
      }
      
      refresh();
    } catch (error) {
      notify(`Error updating ${field}: ${(error as Error).message}`, { type: 'error' });
    } finally {
      setApplying(false);
    }
  };

  const applyAllValues = async () => {
    if (!record || !parsedData) return;
    
    setApplying(true);
    handleConfirmClose();
    
    try {
      const clientUpdates: any = { id: deceasedClient?.id };
      const funeralUpdates: any = { id: record.id };
      
      // Extract all values from parsed data and map to database fields
      if (parsedData.deceased_person) {
        const dp = parsedData.deceased_person;
        if (dp.first_name) {
          const parts = splitGivenName(dp.first_name);
          clientUpdates.first_name = parts.first_name;
          clientUpdates.middle_name = parts.middle_name;
        }
        if (dp.last_name) clientUpdates.last_name = dp.last_name;
        if (dp.date_of_birth) clientUpdates.birthdate = moment(dp.date_of_birth).format('YYYY-MM-DD');
        if (dp.sex) clientUpdates.sex = dp.sex;
        if (dp.place_of_birth) clientUpdates.place_of_birth = dp.place_of_birth;
        if (dp.medicare_number) clientUpdates.medicare_number = dp.medicare_number;
        if (dp.sp3_number) funeralUpdates.sp3_number = dp.sp3_number;
      }
      
      if (parsedData.parent_info) {
        const pi = parsedData.parent_info;
        if (pi.father_first_name) clientUpdates.parent1_first_name = pi.father_first_name;
        if (pi.father_last_name) clientUpdates.parent1_last_name = pi.father_last_name;
        if (pi.mother_first_name) clientUpdates.parent2_first_name = pi.mother_first_name;
        if (pi.mother_last_name) clientUpdates.parent2_last_name = pi.mother_last_name;
      }
      
      if (parsedData.place_of_death) {
        const pod = parsedData.place_of_death;
        if (pod.location) funeralUpdates.place_of_death = pod.location;
        if (pod.code) funeralUpdates.place_of_death_code = pod.code;
        if (pod.address) {
          // Store as JSON object
          funeralUpdates.place_of_death_address = pod.address;
        }
        if (pod.date_time_of_death) {
          const iso = torontoLocalToIso(pod.date_time_of_death);
          if (iso) funeralUpdates.date_time_of_death = iso;
        }
      }
      
      if (parsedData.medical_certification) {
        const mc = parsedData.medical_certification;
        if (mc.doctor_name) funeralUpdates.doctor_name = mc.doctor_name;
      }
      
      // Update client if there are changes
      if (deceasedClient && Object.keys(clientUpdates).length > 1) {
        await dataProvider.update('clients', {
          id: clientUpdates.id,
          data: clientUpdates,
          previousData: deceasedClient
        });
      }
      
      // Update funeral if there are changes
      if (Object.keys(funeralUpdates).length > 1) {
        await dataProvider.update('funerals', {
          id: record.id,
          data: funeralUpdates,
          previousData: record
        });
      }
      
      notify('All SP3 values applied successfully', { type: 'success' });
      refresh();
      handleClose();
    } catch (error) {
      notify(`Error applying all values: ${(error as Error).message}`, { type: 'error' });
    } finally {
      setApplying(false);
    }
  };

  const renderSection = (title: string, data: Record<string, any> | null | undefined) => {
    if (!data) return null;

    const fieldOrderBySection: Record<string, string[]> = {
      parent_info: ['father_last_name', 'father_first_name', 'mother_last_name', 'mother_first_name'],
    };
    const orderedKeys = fieldOrderBySection[title];
    const entries: [string, any][] = orderedKeys
      ? [
          ...orderedKeys.filter((k) => k in data).map((k) => [k, data[k]] as [string, any]),
          ...Object.entries(data).filter(([k]) => !orderedKeys.includes(k)),
        ]
      : Object.entries(data);

    return (
      <>
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          {title}
        </Typography>
        <Table size="small">
          <TableBody>
            {entries.map(([key, value]) => {
              const currentValue = getCurrentValue(title, key);
              // Format address objects for display
              let sp3Value: string;
              if (value === null || value === undefined) {
                sp3Value = '-';
              } else if (typeof value === 'object') {
                // If it's an address object, format it nicely
                if (key === 'address' && value.adresse) {
                  sp3Value = formatAddressObject(value);
                } else {
                  sp3Value = JSON.stringify(value, null, 2);
                }
              } else {
                sp3Value = String(value);
              }
              
              // For date fields, normalize both values to YYYY-MM-DD format for comparison
              let isDifferent = currentValue !== sp3Value && sp3Value !== '-';
              if (key === 'date_of_birth' && sp3Value !== '-' && currentValue !== '-') {
                const normalizedCurrent = formatDateForComparison(currentValue);
                const normalizedSp3 = formatDateForComparison(sp3Value);
                isDifferent = normalizedCurrent !== normalizedSp3;
              }
              // first_name: currentValue is our "first + middle"; compare
              // whitespace/case-insensitively against the SP3 given name so a
              // deceased with a middle name isn't flagged as a false mismatch.
              if (key === 'first_name' && sp3Value !== '-' && currentValue !== '-') {
                const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
                isDifferent = norm(currentValue) !== norm(sp3Value);
              }
              
              return (
                <TableRow key={key}>
                  <TableCell 
                    component="th" 
                    scope="row" 
                    sx={{ 
                      fontWeight: 'bold',
                      width: '20%',
                      verticalAlign: 'top',
                      border: 'none'
                    }}
                  >
                    {key.split('_').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ')}
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: 'pre-wrap',
                      border: 'none',
                      width: '30%',
                      backgroundColor: isDifferent ? '#fff3e0' : 'transparent'
                    }}
                  >
                    {sp3Value}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      border: 'none',
                      width: '30%',
                      backgroundColor: '#f5f5f5'
                    }}
                  >
                    Current: {currentValue}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      border: 'none',
                      width: '20%',
                      textAlign: 'center'
                    }}
                  >
                    {isDifferent && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => applyValue(title, key, value)}
                        disabled={applying}
                      >
                        Use
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Divider sx={{ mt: 2 }} />
      </>
    );
  };

  return (
    <>
      {!hideButton && (
        <Button
          onClick={handleOpen}
          startIcon={<VisibilityIcon />}
          variant="outlined"
          size="small"
        >
          View SP3 Data ({parsedData ? Object.keys(parsedData).filter(key => parsedData[key] && Object.keys(parsedData[key]).length > 0).length : 0} sections)
        </Button>
      )}

      <Dialog
        open={open} 
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          SP3 Data Comparison
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ mt: 1 }}>
            <Chip 
              icon={<CheckIcon />}
              label="SP3 Value" 
              size="small" 
              sx={{ mr: 1, backgroundColor: '#fff3e0' }}
            />
            <Chip 
              label="Current Value" 
              size="small" 
              sx={{ backgroundColor: '#f5f5f5' }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {renderSection("deceased_person", parsedData.deceased_person)}
          {renderSection("parent_info", parsedData.parent_info)}
          {renderSection("place_of_death", parsedData.place_of_death)}
          {renderSection("medical_certification", parsedData.medical_certification)}

          {/* Causes of death — auto-applied, display only */}
          {parsedData.cause_of_death && (
            <>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Causes of Death
              </Typography>
              <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 2 }}>
                {parsedData.cause_of_death.immediate && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>a)</strong> {parsedData.cause_of_death.immediate}
                  </Typography>
                )}
                {parsedData.cause_of_death.underlying && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>b)</strong> {parsedData.cause_of_death.underlying}
                  </Typography>
                )}
                {parsedData.cause_of_death.contributing && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>c)</strong> {parsedData.cause_of_death.contributing}
                  </Typography>
                )}
                {parsedData.cause_of_death.external && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>d)</strong> {parsedData.cause_of_death.external}
                  </Typography>
                )}
                {parsedData.cause_of_death.other && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Other:</strong> {parsedData.cause_of_death.other}
                  </Typography>
                )}
                <Chip label="Auto-applied" size="small" color="success" sx={{ mt: 1 }} />
              </Box>
              <Divider sx={{ mt: 2 }} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClose}>Close</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmOpen}
            disabled={applying}
            startIcon={applying ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            Replace All Different Values
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={handleConfirmClose}>
        <DialogTitle>Confirm Replace All</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to replace all current values with the SP3 data?
            This will update the funeral and client records.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose}>Cancel</Button>
          <Button 
            onClick={applyAllValues} 
            variant="contained" 
            color="primary"
            disabled={applying}
          >
            Yes, Replace All
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 